// shared/api-client/endpoints/votes.js
// Votes API endpoints

import { baseApi } from "../baseApi";

const votesApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // ========================================================================
    // MEMBER ENDPOINTS
    // ========================================================================

    getActiveVotes: builder.query({
      query: () => "/api/votes",
      providesTags: (result) =>
        result?.data?.votes
          ? [
              ...result.data.votes.map(({ id }) => ({ type: "Vote", id })),
              { type: "Vote", id: "LIST" },
            ]
          : [{ type: "Vote", id: "LIST" }],
    }),

    getReleasedVoteResults: builder.query({
      query: () => "/api/votes/results",
      providesTags: [{ type: "Vote", id: "RESULTS" }],
    }),

    getVoteById: builder.query({
      query: (id) => `/api/votes/${id}`,
      providesTags: (result, error, id) => [{ type: "Vote", id }],
    }),

    getMyParticipation: builder.query({
      query: (voteId) => `/api/votes/${voteId}/my-participation`,
      providesTags: (result, error, voteId) => [
        { type: "VoteParticipation", id: voteId },
      ],
    }),

    getVoteResults: builder.query({
      query: (voteId) => `/api/votes/${voteId}/results`,
      providesTags: (result, error, voteId) => [
        { type: "Vote", id: `RESULTS_${voteId}` },
      ],
    }),

    castBallot: builder.mutation({
      query: ({ voteId, selectedOptionIds }) => ({
        url: `/api/votes/${voteId}/ballots`,
        method: "POST",
        body: { selectedOptionIds },
      }),
      invalidatesTags: (result, error, { voteId }) => [
        { type: "Vote", id: voteId },
        { type: "Vote", id: "LIST" },
        { type: "VoteParticipation", id: voteId },
      ],
    }),

    requestPaperBallot: builder.mutation({
      query: (voteId) => ({
        url: `/api/votes/${voteId}/paper-ballots`,
        method: "POST",
      }),
      invalidatesTags: (result, error, voteId) => [
        { type: "Vote", id: `PAPER_BALLOTS_${voteId}` },
      ],
    }),

    submitTestBallot: builder.mutation({
      query: ({ voteId, selectedOptionIds, note }) => ({
        url: `/api/votes/${voteId}/test-ballots`,
        method: "POST",
        body: { selectedOptionIds, note },
      }),
      invalidatesTags: (result, error, { voteId }) => [
        { type: "Vote", id: `TEST_BALLOTS_${voteId}` },
      ],
    }),

    // ========================================================================
    // ADMIN ENDPOINTS
    // ========================================================================

    getAdminVotes: builder.query({
      query: (params = {}) => ({ url: "/api/votes/admin", params }),
      providesTags: (result) =>
        result?.data?.votes
          ? [
              ...result.data.votes.map(({ id }) => ({ type: "Vote", id })),
              { type: "Vote", id: "ADMIN_LIST" },
            ]
          : [{ type: "Vote", id: "ADMIN_LIST" }],
    }),

    createVote: builder.mutation({
      query: (body) => ({ url: "/api/votes", method: "POST", body }),
      invalidatesTags: [
        { type: "Vote", id: "LIST" },
        { type: "Vote", id: "ADMIN_LIST" },
      ],
    }),

    updateVote: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `/api/votes/${id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "Vote", id },
        { type: "Vote", id: "LIST" },
        { type: "Vote", id: "ADMIN_LIST" },
      ],
    }),

    deleteVote: builder.mutation({
      query: (id) => ({ url: `/api/votes/${id}`, method: "DELETE" }),
      invalidatesTags: (result, error, id) => [
        { type: "Vote", id },
        { type: "Vote", id: "LIST" },
        { type: "Vote", id: "ADMIN_LIST" },
      ],
    }),

    submitForReview: builder.mutation({
      query: (id) => ({ url: `/api/votes/${id}/submit-for-review`, method: "PATCH" }),
      invalidatesTags: (result, error, id) => [
        { type: "Vote", id },
        { type: "Vote", id: "LIST" },
        { type: "Vote", id: "ADMIN_LIST" },
      ],
    }),

    activateVote: builder.mutation({
      query: (id) => ({ url: `/api/votes/${id}/activate`, method: "PATCH" }),
      invalidatesTags: (result, error, id) => [
        { type: "Vote", id },
        { type: "Vote", id: "LIST" },
        { type: "Vote", id: "ADMIN_LIST" },
      ],
    }),

    closeVote: builder.mutation({
      query: (id) => ({ url: `/api/votes/${id}/close`, method: "PATCH" }),
      invalidatesTags: (result, error, id) => [
        { type: "Vote", id },
        { type: "Vote", id: "LIST" },
        { type: "Vote", id: "ADMIN_LIST" },
      ],
    }),

    releaseVoteResults: builder.mutation({
      query: (id) => ({ url: `/api/votes/${id}/release`, method: "PATCH" }),
      invalidatesTags: (result, error, id) => [
        { type: "Vote", id },
        { type: "Vote", id: "LIST" },
        { type: "Vote", id: "ADMIN_LIST" },
        { type: "Vote", id: "RESULTS" },
        { type: "Vote", id: `RESULTS_${id}` },
      ],
    }),

    getAuditBallots: builder.query({
      query: (voteId) => `/api/votes/${voteId}/audit`,
      providesTags: (result, error, voteId) => [
        { type: "Vote", id: `AUDIT_${voteId}` },
      ],
    }),

    extendVote: builder.mutation({
      query: ({ id, closeAt }) => ({
        url: `/api/votes/${id}/extend`,
        method: "PATCH",
        body: { closeAt },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "Vote", id },
        { type: "Vote", id: "LIST" },
        { type: "Vote", id: "ADMIN_LIST" },
      ],
    }),

    getParticipationStats: builder.query({
      query: (voteId) => `/api/votes/${voteId}/participation-stats`,
      providesTags: (result, error, voteId) => [
        { type: "Vote", id: `STATS_${voteId}` },
      ],
    }),

    getVoterRoll: builder.query({
      query: (voteId) => `/api/votes/${voteId}/voter-roll`,
      providesTags: (result, error, voteId) => [
        { type: "Vote", id: `VOTER_ROLL_${voteId}` },
      ],
    }),

    listObservers: builder.query({
      query: (voteId) => `/api/votes/${voteId}/observers`,
      providesTags: (result, error, voteId) => [
        { type: "Vote", id: `OBSERVERS_${voteId}` },
      ],
    }),

    addObserver: builder.mutation({
      query: ({ voteId, userId }) => ({
        url: `/api/votes/${voteId}/observers`,
        method: "POST",
        body: { userId },
      }),
      invalidatesTags: (result, error, { voteId }) => [
        { type: "Vote", id: `OBSERVERS_${voteId}` },
      ],
    }),

    removeObserver: builder.mutation({
      query: ({ voteId, userId }) => ({
        url: `/api/votes/${voteId}/observers/${userId}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, { voteId }) => [
        { type: "Vote", id: `OBSERVERS_${voteId}` },
      ],
    }),

    listEligibilityChallenges: builder.query({
      query: (voteId) => `/api/votes/${voteId}/challenges`,
      providesTags: (result, error, voteId) => [
        { type: "Vote", id: `CHALLENGES_${voteId}` },
      ],
    }),

    createEligibilityChallenge: builder.mutation({
      query: ({ voteId, targetUserId, reason }) => ({
        url: `/api/votes/${voteId}/challenges`,
        method: "POST",
        body: { targetUserId, reason },
      }),
      invalidatesTags: (result, error, { voteId }) => [
        { type: "Vote", id: `CHALLENGES_${voteId}` },
      ],
    }),

    resolveEligibilityChallenge: builder.mutation({
      query: ({ voteId, cId, status, resolution }) => ({
        url: `/api/votes/${voteId}/challenges/${cId}`,
        method: "PATCH",
        body: { status, resolution },
      }),
      invalidatesTags: (result, error, { voteId }) => [
        { type: "Vote", id: `CHALLENGES_${voteId}` },
      ],
    }),

    listPaperBallots: builder.query({
      query: (voteId) => `/api/votes/${voteId}/paper-ballots`,
      providesTags: (result, error, voteId) => [
        { type: "Vote", id: `PAPER_BALLOTS_${voteId}` },
      ],
    }),

    updatePaperBallot: builder.mutation({
      query: ({ voteId, pbId, status, notes }) => ({
        url: `/api/votes/${voteId}/paper-ballots/${pbId}`,
        method: "PATCH",
        body: { status, notes },
      }),
      invalidatesTags: (result, error, { voteId }) => [
        { type: "Vote", id: `PAPER_BALLOTS_${voteId}` },
      ],
    }),

    getTestBallots: builder.query({
      query: (voteId) => `/api/votes/${voteId}/test-ballots`,
      providesTags: (result, error, voteId) => [
        { type: "Vote", id: `TEST_BALLOTS_${voteId}` },
      ],
    }),

    purgeTestBallots: builder.mutation({
      query: (voteId) => ({
        url: `/api/votes/${voteId}/test-ballots`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, voteId) => [
        { type: "Vote", id: `TEST_BALLOTS_${voteId}` },
      ],
    }),

    getEligibleVoters: builder.query({
      query: (voteId) => `/api/votes/${voteId}/voter-roll`,
      providesTags: (result, error, voteId) => [
        { type: "Vote", id: `VOTER_ROLL_${voteId}` },
      ],
    }),

    addEligibleVoter: builder.mutation({
      query: ({ voteId, ...body }) => ({
        url: `/api/votes/${voteId}/eligible-voters`,
        method: "POST",
        body,
      }),
      invalidatesTags: (result, error, { voteId }) => [
        { type: "Vote", id: `VOTER_ROLL_${voteId}` },
      ],
    }),

    removeEligibleVoter: builder.mutation({
      query: ({ voteId, evId }) => ({
        url: `/api/votes/${voteId}/eligible-voters/${evId}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, { voteId }) => [
        { type: "Vote", id: `VOTER_ROLL_${voteId}` },
      ],
    }),

    postponeVote: builder.mutation({
      query: ({ voteId, postponedUntil, postponeReason }) => ({
        url: `/api/votes/${voteId}/postpone`,
        method: "PATCH",
        body: { postponedUntil, postponeReason },
      }),
      invalidatesTags: (result, error, { voteId }) => [
        { type: "Vote", id: voteId },
        { type: "Vote", id: "ADMIN_LIST" },
      ],
    }),

    // Returns a Blob — caller must use responseHandler: 'blob' via fetchBaseQuery
    exportVoteRecords: builder.query({
      query: (voteId) => ({
        url: `/api/votes/${voteId}/export`,
        responseHandler: (response) => response.blob(),
      }),
    }),
  }),
});

// Member hooks
export const useGetActiveVotesQuery = votesApi.useGetActiveVotesQuery;
export const useGetReleasedVoteResultsQuery = votesApi.useGetReleasedVoteResultsQuery;
export const useGetVoteByIdQuery = votesApi.useGetVoteByIdQuery;
export const useGetMyParticipationQuery = votesApi.useGetMyParticipationQuery;
export const useGetVoteResultsQuery = votesApi.useGetVoteResultsQuery;
export const useCastBallotMutation = votesApi.useCastBallotMutation;
export const useRequestPaperBallotMutation = votesApi.useRequestPaperBallotMutation;
export const useSubmitTestBallotMutation = votesApi.useSubmitTestBallotMutation;

// Admin hooks
export const useGetAdminVotesQuery = votesApi.useGetAdminVotesQuery;
export const useCreateVoteMutation = votesApi.useCreateVoteMutation;
export const useUpdateVoteMutation = votesApi.useUpdateVoteMutation;
export const useDeleteVoteMutation = votesApi.useDeleteVoteMutation;
export const useSubmitForReviewMutation = votesApi.useSubmitForReviewMutation;
export const useActivateVoteMutation = votesApi.useActivateVoteMutation;
export const useCloseVoteMutation = votesApi.useCloseVoteMutation;
export const useReleaseVoteResultsMutation = votesApi.useReleaseVoteResultsMutation;
export const useGetAuditBallotsQuery = votesApi.useGetAuditBallotsQuery;
export const useExtendVoteMutation = votesApi.useExtendVoteMutation;
export const useGetParticipationStatsQuery = votesApi.useGetParticipationStatsQuery;
export const useGetVoterRollQuery = votesApi.useGetVoterRollQuery;
export const useListObserversQuery = votesApi.useListObserversQuery;
export const useAddObserverMutation = votesApi.useAddObserverMutation;
export const useRemoveObserverMutation = votesApi.useRemoveObserverMutation;
export const useListEligibilityChallengesQuery = votesApi.useListEligibilityChallengesQuery;
export const useCreateEligibilityChallengeMutation = votesApi.useCreateEligibilityChallengeMutation;
export const useResolveEligibilityChallengeMutation = votesApi.useResolveEligibilityChallengeMutation;
export const useListPaperBallotsQuery = votesApi.useListPaperBallotsQuery;
export const useUpdatePaperBallotMutation = votesApi.useUpdatePaperBallotMutation;
export const useGetTestBallotsQuery = votesApi.useGetTestBallotsQuery;
export const usePurgeTestBallotsMutation = votesApi.usePurgeTestBallotsMutation;
export const useGetEligibleVotersQuery = votesApi.useGetEligibleVotersQuery;
export const useAddEligibleVoterMutation = votesApi.useAddEligibleVoterMutation;
export const useRemoveEligibleVoterMutation = votesApi.useRemoveEligibleVoterMutation;
export const usePostponeVoteMutation = votesApi.usePostponeVoteMutation;
export const useExportVoteRecordsQuery = votesApi.useExportVoteRecordsQuery;
